import {Icon, TextInput, Tooltip} from '@/components/atoms'
import {defaultTo} from 'lodash'
import {useCallback, useEffect, useState} from 'react'
import './text-input.scss'
import useTextInputValidation from './useTextInputValidation-hook'
/**
 * TextInputField component renders a text input field with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the input field.
 * @param {function} props.updateValue - Function to update the value of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {Object} props.schema - The schema defining the input field type and validation.
 * @param {Object} props.uiSchema - The UI schema defining additional UI properties.
 * @param {boolean} [props.required=false] - Whether the input field is required.
 * @param {boolean} [props.disabled=false] - Whether the input field is disabled.
 * @param {boolean} props.visible - Whether the input field is visible.
 *
 * @returns {JSX.Element|null} The rendered TextInputField component or null if not visible.
 */

const TextInputField = ({value, updateValue, label, schema, uiSchema, required = false, disabled = false, visible}) => {
  const {onChange} = useTextInputValidation(label, uiSchema?.validationRules)
  const [error, setError] = useState()
  const [lastValidValue, setLastValidValue] = useState(value)

  const clearValidationError = val => {
    const validationError = onChange(val)
    if (!validationError) {
      setError(undefined)
      setLastValidValue(val)
      updateValue(val, false)
    }
  }

  const handleValidationError = useCallback(
    (errorMessage, val) => {
      setError(errorMessage)
      updateValue(val, true)
    },
    [updateValue]
  )

  const handleOnChange = e => {
    const newValue = typeof e === 'string' ? e : e?.target?.value
    const validationError = onChange(newValue)
    if (validationError) {
      handleValidationError(validationError, newValue)
    } else if (error || newValue !== lastValidValue) {
      clearValidationError(newValue)
    }
  }

  useEffect(() => {
    // validate default value
    if (value || value === '') {
      handleOnChange(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const getInputType = schema => {
    return schema?.type === 'integer' ? 'number' : schema?.type || 'text'
  }

  const handleKeyDown = e => {
    const {key} = e
    if (schema?.restrictDecimal) {
      if (['.', ',', 'e', '-'].includes(key) || (!value && key === '0')) {
        e.preventDefault()
      }
    } else if (
      (schema?.type === 'number' && ['e', 'E', '+', '-'].includes(key)) ||
      ['ArrowUp', 'ArrowDown'].includes(key)
    ) {
      e.preventDefault()
    }
  }

  const disableButton = disabled || uiSchema?.readOnly ? 'disable-button' : ''

  return visible ? (
    <>
      <div className={`input-text-wrapper ${uiSchema?.className}`}>
        <TextInput
          required={required}
          className={`${uiSchema?.className ?? ''} ${disableButton} inputType-${getInputType(schema)}`}
          disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
          maxLength={uiSchema?.maxLength ?? undefined}
          type={getInputType(schema)}
          error={error}
          label={label}
          onChange={handleOnChange}
          isDark={uiSchema?.isDark || false}
          value={defaultTo(!value ? '' : value, undefined)}
          placeholderText={uiSchema?.placeholder ?? ''}
          helperText={uiSchema?.helperText}
          handleKeyDown={handleKeyDown}
        />
        {uiSchema?.tooltipText && uiSchema?.tooltipText?.length > 0 && (
          <div className="tooltip-text">
            <Tooltip text={uiSchema.tooltipText}>
              <Icon alt="" icon="info" size="small" />
            </Tooltip>
          </div>
        )}
      </div>

      {!error && uiSchema.helpText && (
        <div className="input-help-text items-center">
          <Tooltip text={uiSchema.helpText} className="help-tooltip">
            <Icon icon="info" size="small" />
          </Tooltip>
          {uiSchema.helpText ? uiSchema.helpText : 'Help'}
        </div>
      )}
    </>
  ) : null
}

export default TextInputField
